import Select from 'react-select';

import clsx from 'clsx';

import { Label, LABEL_TYPE, OptionInfoIcon, Tag } from '@/components/generic';
import { COLORS } from '@/enums';
import { SelectOptionProps, SelectProps } from '@/types';

export const SingleSelect = ({
  id,
  label,
  options,
  handleChange,
  errorMessage,
  disabled,
  value,
  defaultValue,
  placeholder,
  customPlaceholder,
  isClearable,
  isSearchable = false,
  isInvalid,
  handleInputChange,
  isSidebar,
  className,
  isRequired,
  containerClassName,
  textColor,
  borderStyle,
  fontWeight,
  controlStyles,
  icon,
  isLiveStream,
  menuIsOpen,
  onMenuOpen,
  onMenuClose,
  dropdownIndicatorColor,
  fetchNextPage,
  hasNextPage,
  onBlur,
  placeholderStyles,
  testId
}: SelectProps) => {
  return (
    <div data-testid={testId} className={`${containerClassName}`}>
      {label && (
        <Label
          text={`${label}${isRequired ? ' *' : ''}`}
          type={LABEL_TYPE.PARAGRAPH2}
          className='mb-2.5'
        />
      )}
      <Select
        menuPortalTarget={document.body}
        className={className}
        isClearable={isClearable}
        isDisabled={disabled}
        name={id}
        id={id}
        defaultValue={defaultValue}
        value={value}
        onChange={handleChange}
        isSearchable={isSearchable}
        options={options as SelectOptionProps[]}
        onInputChange={handleInputChange}
        menuPlacement='auto'
        menuIsOpen={menuIsOpen}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        onBlur={onBlur}
        onMenuScrollToBottom={() => {
          if (hasNextPage) {
            fetchNextPage?.();
          }
        }}
        formatOptionLabel={(option) => (
          <div className='flex flex-row items-center gap-2'>
            {option.image ? <img src={option.image} className='size-8' /> : null}
            {option.icon && option.icon}
            {option.color && (
              <span
                className='size-6 rounded-md border border-gray-300'
                style={{ backgroundColor: option.color }}
              />
            )}
            <span
              className={clsx({
                'flex w-full flex-col py-2 !text-sm !font-semibold !text-primary-blue': isLiveStream
              })}>
              {option.label}
              {option.description && (
                <span className='!text-2xs !text-secondary-gray'>{option.description}</span>
              )}
            </span>
            {option.tag ? (
              <Tag
                value={option.tag as string}
                statusDescription={option.statusDescription as string}
              />
            ) : null}
            {option.infoText && <OptionInfoIcon text={option.infoText} />}
          </div>
        )}
        components={{
          Placeholder: () => (
            <div
              style={{
                color:
                  textColor ||
                  (isLiveStream && COLORS.PRIMARY_BLUE) ||
                  (isSearchable && COLORS.SECONDARY_GRAY) ||
                  (isSidebar && COLORS.PRIMARY_WHITE) ||
                  COLORS.SECONDARY_GRAY,
                display: 'flex',
                alignItems: 'center',
                marginTop: '-24px',
                marginLeft: isSidebar ? '10px' : '5px',
                strokeWidth: isSidebar ? '1' : '0',
                ...placeholderStyles
              }}>
              {customPlaceholder ? (
                customPlaceholder
              ) : (
                <>
                  {icon}
                  <Label
                    text={placeholder}
                    type={isSidebar ? LABEL_TYPE.PARAGRAPH1 : LABEL_TYPE.PARAGRAPH2}
                    className={`${isSidebar && 'font-semibold text-white'} ${
                      isLiveStream && 'font-semibold'
                    } ${fontWeight && fontWeight}`}
                  />
                </>
              )}
            </div>
          )
        }}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          control: (base, state) => ({
            ...base,
            minHeight: controlStyles?.minHeight ?? '40px',
            cursor: 'pointer',
            height: controlStyles?.height ?? '40px',
            borderRadius: isLiveStream ? 'none' : '10px',
            borderTopLeftRadius: isLiveStream ? '10px' : base.borderTopLeftRadius,
            borderTopRightRadius: isLiveStream ? '10px' : base.borderTopRightRadius,
            borderBottomRightRadius:
              isLiveStream && !state.menuIsOpen ? '10px' : base.borderBottomRightRadius,
            borderBottomLeftRadius:
              isLiveStream && !state.menuIsOpen ? '10px' : base.borderBottomLeftRadius,
            fontWeight: fontWeight ?? '400',
            fontSize: `${isSidebar ? '16px' : '14px'}`,
            color: textColor ?? base.color,
            backgroundColor: `${isSidebar ? COLORS.PRIMARY_BLUE : controlStyles?.backgroundColor}`,
            border:
              borderStyle ||
              (isLiveStream && 'none') ||
              (state.isFocused && `1px solid ${COLORS.PRIMARY_BLUE}`) ||
              (isInvalid && `1px solid ${COLORS.ACCENT_RED}`) ||
              `1px solid ${COLORS.SECONDARY_BLUE_GRAY}`,
            borderLeft: isLiveStream ? `1px solid ${COLORS.PRIMARY_BLUE}` : base.borderLeft,
            borderRight: isLiveStream ? `1px solid ${COLORS.PRIMARY_BLUE}` : base.borderRight,
            borderTop: isLiveStream ? `1px solid ${COLORS.PRIMARY_BLUE}` : base.borderTop,
            borderBottom:
              isLiveStream && !state.menuIsOpen
                ? `1px solid ${COLORS.PRIMARY_BLUE}`
                : base.borderTop,
            boxShadow: 'none',
            '&:hover': {
              border: `1px solid ${COLORS.PRIMARY_BLUE}`,
              borderBottom:
                isLiveStream && state.menuIsOpen ? '0' : `1px solid ${COLORS.PRIMARY_BLUE}`
            }
          }),
          singleValue: (base) => ({
            ...base,
            color: isSidebar ? COLORS.PRIMARY_WHITE : COLORS.PRIMARY_BLACK,
            padding: '0 5px'
          }),
          input: (base) => ({
            ...base,
            color: isSidebar ? COLORS.PRIMARY_WHITE : COLORS.PRIMARY_BLACK,
            padding: '0 5px'
          }),
          indicatorSeparator: (base) => ({
            ...base,
            display: isLiveStream ? 'block' : 'none'
          }),
          dropdownIndicator: (base, state) => ({
            ...base,
            color:
              (isLiveStream && COLORS.PRIMARY_BLUE) ||
              (isSidebar && COLORS.PRIMARY_WHITE) ||
              (state.selectProps.value && COLORS.PRIMARY_BLACK) ||
              (dropdownIndicatorColor && dropdownIndicatorColor) ||
              COLORS.SECONDARY_GRAY,
            transition: 'all .2s ease',
            transform: state.isFocused && state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
            '&:hover': {
              color:
                (isLiveStream && COLORS.PRIMARY_BLUE) ||
                (isSidebar && COLORS.PRIMARY_WHITE) ||
                (state.selectProps.value && COLORS.PRIMARY_BLACK) ||
                (dropdownIndicatorColor && dropdownIndicatorColor) ||
                COLORS.SECONDARY_GRAY
            }
          }),
          clearIndicator: (base) => {
            let colorValue;
            if (textColor) {
              colorValue = textColor;
            } else if (isSearchable) {
              colorValue = COLORS.SECONDARY_GRAY;
            } else {
              colorValue = base.color;
            }
            return {
              ...base,
              padding: '0 5px',
              color: colorValue,
              fontWeight: fontWeight ?? base.fontWeight,
              '&:hover': {
                color: isSidebar ? COLORS.PRIMARY_WHITE : COLORS.PRIMARY_BLACK
              }
            };
          },
          menu: (base) => ({
            ...base,
            borderLeft: isLiveStream ? `1px solid ${COLORS.PRIMARY_BLUE}` : 'none',
            borderRight: isLiveStream ? `1px solid ${COLORS.PRIMARY_BLUE}` : 'none',
            borderBottom: isLiveStream ? `1px solid ${COLORS.PRIMARY_BLUE}` : 'none',
            boxShadow: isLiveStream ? 'none' : `0 0 8px 0 ${COLORS.PRIMARY_BLUE}`,
            borderRadius: isLiveStream ? 'none' : '5px',
            borderBottomLeftRadius: isLiveStream ? '10px' : 'none',
            borderBottomRightRadius: isLiveStream ? '10px' : 'none',
            marginTop: isLiveStream ? '0' : base.marginTop
          }),
          menuList: (base) => ({
            ...base,
            margin: isLiveStream ? '0 10px' : '0 5px',
            cursor: 'pointer',
            '::-webkit-scrollbar': {
              width: '8px',
              height: '0px'
            },
            '::-webkit-scrollbar-track': {
              background: COLORS.PRIMARY_GRAY,
              margin: '16px'
            },
            '::-webkit-scrollbar-thumb': {
              background: COLORS.SECONDARY_STEEL_BLUE,
              borderRadius: '8px'
            },
            '::-webkit-scrollbar-thumb:hover': {
              background: COLORS.PRIMARY_BLUE
            }
          }),
          option: (base, state) => ({
            ...base,
            cursor: 'pointer',
            backgroundColor: state.isSelected ? COLORS.PRIMARY_GRAY : COLORS.PRIMARY_WHITE,
            color: state.isSelected ? COLORS.PRIMARY_BLACK : COLORS.SECONDARY_GRAY,
            fontWeight: state.isSelected ? '600' : '400',
            fontSize: `${isSidebar ? '16px' : '14px'}`,
            width: 'auto',
            height: 'fit-content',
            borderRadius: '5px',
            margin: (() => {
              const numberOfElements = state.selectProps?.options?.length || 0;
              return numberOfElements > 6 ? '5px 5px 5px 0' : '5px 0';
            })() as string,
            padding: isLiveStream ? '0px' : base.padding,
            ...(state.data.hasDivider && {
              position: 'relative',
              marginBottom: '11px',
              '::after': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: '-6px',
                height: '1px',
                backgroundColor: COLORS.SECONDARY_BLUE_GRAY
              }
            }),
            ':hover': {
              backgroundColor: COLORS.PRIMARY_GRAY,
              color: COLORS.PRIMARY_BLACK,
              fontWeight: state.isSelected ? '600' : '400'
            }
          })
        }}
      />
      <div className='flex w-full justify-end'>
        <Label text={isInvalid && errorMessage} type={LABEL_TYPE.PARAGRAPH_ERROR} />
      </div>
    </div>
  );
};
